import { useMemo } from "react";
import { AlertTriangle } from "lucide-react";
import { buildConcentration } from "./analytics";
import { cn } from "../../utilities/helpers";
import { useCurrency } from "../dropdown";
import {
	formatCompactCurrency,
	formatCurrency,
	humanizeAssetLabel,
	normalizeAsset,
	useAssetPalette,
	useInvestmentSummaryQuery,
} from "./shared";

// Wording for each HHI band. The index is the honest measure (it weighs the
// whole book, not just the top three), but "1,550" means nothing on its own, so
// every band is stated as an equivalent number of equally sized positions.
const CONCENTRATION_LEVELS = {
	DIVERSIFIED: {
		label: "Diversified",
		tone: "bg-emerald-50 text-emerald-700",
		alert: false,
	},
	MODERATE: {
		label: "Moderately concentrated",
		tone: "bg-amber-50 text-amber-700",
		alert: true,
	},
	CONCENTRATED: {
		label: "Concentrated",
		tone: "bg-amber-50 text-amber-700",
		alert: true,
	},
};

const ConcentrationStat = ({ label, value, hint, title }) => (
	<div className="min-w-0">
		<dt className="text-xs font-medium text-slate-500" title={title}>
			{label}
		</dt>
		<dd className="mt-0.5 text-sm font-semibold tabular-nums text-slate-900">{value}</dd>
		{hint && <dd className="text-xs text-slate-500">{hint}</dd>}
	</div>
);

const Allocation = () => {
	const { currency } = useCurrency();
	const paletteFor = useAssetPalette();
	const { data: summary, isLoading } = useInvestmentSummaryQuery();

	const { holdings, total } = useMemo(() => {
		const positions = (summary?.assets || [])
			.map((assetSummary) => ({
				asset: normalizeAsset(assetSummary.asset),
				label: humanizeAssetLabel(assetSummary.asset),
				value: Number(assetSummary.currentWorth || 0),
			}))
			// Closed or worthless positions have no share of the portfolio.
			.filter((position) => position.value > 0.005);
		const totalValue = positions.reduce((sum, position) => sum + position.value, 0);

		return {
			total: totalValue,
			holdings: positions
				.map((position) => ({
					...position,
					weight: totalValue > 0 ? (position.value / totalValue) * 100 : 0,
				}))
				.sort((left, right) => right.value - left.value),
		};
	}, [summary]);

	const concentration = useMemo(() => buildConcentration(holdings), [holdings]);
	const largest = holdings[0];
	const level = CONCENTRATION_LEVELS[concentration.level] || CONCENTRATION_LEVELS.DIVERSIFIED;

	return (
		<section className="rounded-xl border border-slate-200 bg-white shadow-sm">
			<div className="p-4 pb-3 md:px-6 md:pt-5">
				<h2 className="text-lg font-semibold text-slate-900">Allocation</h2>
				<p className="text-sm text-slate-500">
					{holdings.length
						? `Share of ${formatCurrency(total, currency)} across ${holdings.length} ${
								holdings.length === 1 ? "holding" : "holdings"
						  }.`
						: "Share of current market value by holding."}
				</p>
			</div>

			<div className="px-4 pb-5 md:px-6">
				{isLoading && !summary && (
					<div className="space-y-3" aria-hidden="true">
						{Array.from({ length: 5 }).map((_, index) => (
							<div key={index} className="h-5 animate-pulse rounded bg-slate-100" />
						))}
					</div>
				)}

				{summary && !holdings.length && (
					<p className="py-6 text-center text-sm text-slate-500">No open positions yet.</p>
				)}

				{largest && (
					<p className="mb-4 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
						<span className="font-medium text-slate-900">{largest.label}</span> is the
						largest holding at{" "}
						<span className="font-medium tabular-nums text-slate-900">
							{largest.weight.toFixed(1)}%
						</span>
						{holdings.length > 3 && (
							<>
								; the top 3 make up{" "}
								<span className="font-medium tabular-nums text-slate-900">
									{concentration.topShare.three.toFixed(1)}%
								</span>{" "}
								of the portfolio
							</>
						)}
						.
					</p>
				)}

				<ul className="space-y-1">
					{holdings.map((holding) => {
						const palette = paletteFor(holding.asset);
						// Bars share one scale anchored to the largest holding; the
						// percentage label carries the absolute share.
						const barWidth = largest?.weight ? (holding.weight / largest.weight) * 100 : 0;

						return (
							<li
								key={holding.asset}
								className="grid grid-cols-[minmax(0,5.5rem)_minmax(0,1fr)_auto] items-center gap-x-3 py-1.5 sm:grid-cols-[minmax(0,6.5rem)_minmax(0,1fr)_auto]"
							>
								<div className="flex min-w-0 items-center gap-2">
									<span
										aria-hidden="true"
										className={cn("h-2.5 w-2.5 shrink-0 rounded-full", palette.dot)}
									/>
									<span className="truncate text-sm font-medium text-slate-900">
										{holding.label}
									</span>
								</div>
								<div aria-hidden="true" className="min-w-0">
									<div
										className="h-2 rounded-full"
										style={{
											width: `${Math.max(barWidth, 1)}%`,
											backgroundColor: palette.line,
										}}
									/>
								</div>
								<div className="flex items-baseline justify-end gap-3 text-sm tabular-nums">
									<span className="w-12 text-right font-medium text-slate-900">
										{holding.weight.toFixed(1)}%
									</span>
									<span
										className="w-16 text-right text-slate-500"
										title={formatCurrency(holding.value, currency)}
									>
										{formatCompactCurrency(holding.value, currency)}
									</span>
								</div>
							</li>
						);
					})}
				</ul>

				{concentration.count > 0 && (
					<div className="mt-5 border-t border-slate-100 pt-4">
						<div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
							<h3 className="text-sm font-medium text-slate-900">Concentration</h3>
							<span
								className={cn(
									"rounded-md px-2 py-0.5 text-xs font-medium",
									level.tone
								)}
							>
								{level.label}
							</span>
						</div>

						<dl className="mt-3 grid grid-cols-3 gap-x-4">
							<ConcentrationStat
								label="Top 3"
								value={`${concentration.topShare.three.toFixed(1)}%`}
								hint={`of ${concentration.count} holdings`}
							/>
							<ConcentrationStat
								label="Spread like"
								value={concentration.effectivePositions.toFixed(1)}
								hint="equal positions"
								title="How many equally sized holdings would be exactly this concentrated. Lower than the actual count means the weight sits in a few names."
							/>
							<ConcentrationStat
								label="HHI"
								value={Math.round(concentration.hhi).toLocaleString("en-US")}
								hint="0-10,000 scale"
								title="Herfindahl-Hirschman index: the sum of each holding's squared percentage weight. Below 1,500 reads as diversified, 1,500-2,500 as moderate, above 2,500 as concentrated."
							/>
						</dl>

						{level.alert && (
							<p className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
								<AlertTriangle
									aria-hidden="true"
									className="mt-0.5 h-4 w-4 shrink-0"
								/>
								<span>
									The weight sits in roughly{" "}
									<span className="font-medium tabular-nums">
										{concentration.effectivePositions.toFixed(1)}
									</span>{" "}
									positions rather than {concentration.count}. A move in{" "}
									{largest?.label} carries {largest?.weight.toFixed(0)}% of the
									portfolio with it.
								</span>
							</p>
						)}
					</div>
				)}
			</div>
		</section>
	);
};

export default Allocation;
